import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from '../homepage/footer';
import { useNavigate } from 'react-router-dom';

const Landing = () => {
  const navigate = useNavigate();

  // Topic data with SVG icons
  const topics = [
    {
      name: 'Algebra',
      description: 'Solving equations, algebraic manipulation and sequences',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Geometry',
      description: 'Properties of shapes, angles and transformations',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
        </svg>
      )
    },
    {
      name: 'Number',
      description: 'Fractions, decimals, powers and roots',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      )
    },
    {
      name: 'Ratio',
      description: 'Ratio, proportion and percentages',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
        </svg>
      )
    },
    {
      name: 'Probability',
      description: 'Theoretical and experimental probability',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      )
    },
    {
      name: 'Statistics',
      description: 'Averages, spread and data representation',
      icon: (
        <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-800">
      <Helmet>
        <title>MLMaths - GCSE Maths Revision & Practice Online</title>
        <link rel="canonical" href="https://mlmaths.co.uk/" />
        <meta name="description" content="Master GCSE Maths with MLMaths. Interactive lessons, quizzes, and exam practice for Foundation and Higher levels. Start your journey today!" />
      </Helmet>

      {/* Hero Section with Integrated Header */}
      <section className="relative flex flex-col bg-gradient-to-br from-teal-500 via-teal-600 to-teal-700 text-white py-8 px-6">
        {/* Decorative circles */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-white opacity-10 rounded-full blur-3xl"></div>
        
        {/* Logo at top */}
        <div className="relative z-20 mb-12">
          <div className="text-3xl font-bold text-white">MLMaths</div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center pb-16">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
            Master GCSE Maths with Confidence
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-teal-50 max-w-2xl mx-auto">
            Interactive lessons, practice quizzes, and progress tracking — everything you need to excel in your exams.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate('/signup')}
              className="bg-white text-teal-700 px-8 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 transform"
            >
              Get Started
            </button>
            <button
              onClick={() => navigate('/signin')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-teal-700 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 transform"
            >
              Sign In
            </button>
          </div>
          
          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-teal-50">
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-semibold">Foundation & Higher</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">All Exam Boards</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span className="font-semibold">Comprehensive Resources</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <main className="container mx-auto px-6 py-20">
        <section className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Why Choose MLMaths?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to succeed in your GCSE Maths journey, all in one place.
          </p>
        </section>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Feature 1 */}
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-teal-500">
            <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Track Your Progress</h3>
            <p className="text-gray-600 leading-relaxed">
              Visualise your learning journey with detailed analytics, personalised feedback, and achievement milestones.
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-teal-500">
            <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Interactive Learning</h3>
            <p className="text-gray-600 leading-relaxed">
              Engage with interactive lessons, video tutorials, and practice quizzes designed for deep understanding.
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-teal-500">
            <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Comprehensive Coverage</h3>
            <p className="text-gray-600 leading-relaxed">
              Access complete coverage of all topics from Foundation to Higher level, aligned with exam boards.
            </p>
          </div>
        </div>

        {/* Enhanced Topics Section with SVG Icons */}
        <section className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Master Every Topic
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From Algebra to Statistics, comprehensive coverage of all GCSE Maths topics with video lessons, worked examples, and practice questions.
            </p>
          </div>
          
          {/* Topics Grid with SVG Icons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            {topics.map((topic) => (
              <div 
                key={topic.name} 
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-teal-500 group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    {topic.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-teal-600 transition-colors">
                      {topic.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {topic.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Exam Boards Section */}
          <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-3xl p-10 text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Supporting All Major Exam Boards
            </h3>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Content aligned with Edexcel, AQA, and OCR specifications for both Foundation and Higher tiers.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="bg-white px-8 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl font-bold text-gray-800">Edexcel</span>
              </div>
              <div className="bg-white px-8 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl font-bold text-gray-800">AQA</span>
              </div>
              <div className="bg-white px-8 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl font-bold text-gray-800">OCR</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Landing;